import { useSearchParams } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle, Clock } from "lucide-react";
import PageHero from "../components/sections/PageHero";
import ContactForm from "../components/sections/ContactForm";
import { BRAND, waLink, telLink } from "../lib/brand";

export default function Contact() {
  const [sp] = useSearchParams();
  const service = sp.get("service");
  const defaultService = service === "inspection" ? "" : service || "";
  const leadType = service === "inspection" ? "site_inspection" : "enquiry";

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Let's discuss"
        italic="your project."
        subtitle="Call, WhatsApp, email or request a free site inspection. Our team responds within the same working day."
        image="https://customer-assets-jai6qajn.emergentagent.net/job_painter-showcase-v1/artifacts/wkrh8l47_Screenshot%202025-01-29%20071934.png"
      />

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-5">
            <h2 className="font-display text-2xl md:text-3xl text-ink leading-tight tracking-[-0.02em]">
              {BRAND.name}
            </h2>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-ink-muted mt-2">
              {BRAND.tagline}
            </div>

            <ul className="mt-8 space-y-4">
              <ContactLine
                Icon={Phone}
                label="Phone"
                value={BRAND.phone}
                href={telLink()}
                testid="contact-phone"
              />
              <ContactLine
                Icon={MessageCircle}
                label="WhatsApp"
                value={BRAND.phone}
                href={waLink()}
                testid="contact-whatsapp"
              />
              <ContactLine
                Icon={Mail}
                label="Email"
                value={BRAND.email}
                href={`mailto:${BRAND.email}`}
                testid="contact-email"
              />
              <ContactLine
                Icon={Clock}
                label="Business Hours"
                value={BRAND.hours}
                testid="contact-hours"
              />
              <ContactLine
                Icon={MapPin}
                label="Location"
                value={BRAND.address}
                testid="contact-address"
              />
            </ul>

            <div className="mt-8 border border-ink-line">
              <div className="p-4 border-b border-ink-line">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-ink-muted">
                  Service Areas
                </div>
                <div className="mt-2 text-sm text-ink leading-relaxed">
                  {BRAND.service_area.join(" · ")}
                </div>
              </div>
              <div className="aspect-[16/11]">
                <iframe
                  title="Urban Painter location"
                  src={BRAND.mapEmbed}
                  className="w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-2">
              <a
                href={telLink()}
                data-testid="contact-call-btn"
                className="bg-ink hover:bg-ink-soft text-white h-12 inline-flex items-center justify-center text-[11px] tracking-[0.25em] uppercase"
              >
                Call Now
              </a>
              <a
                href={waLink()}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-btn"
                className="border border-ink text-ink hover:bg-ink hover:text-white h-12 inline-flex items-center justify-center text-[11px] tracking-[0.25em] uppercase transition-colors"
              >
                WhatsApp
              </a>
            </div>
          </aside>

          <div className="lg:col-span-7 lg:pl-10 lg:border-l lg:border-ink-line">
            <ContactForm
              defaultService={defaultService}
              leadType={leadType}
              source="contact_page"
            />
          </div>
        </div>
      </section>
    </>
  );
}

function ContactLine({ Icon, label, value, href, testid }) {
  const Content = (
    <div className="flex items-start gap-4 group">
      <div className="w-11 h-11 grid place-items-center bg-ink-mist border border-ink-line group-hover:bg-ink group-hover:text-white transition-colors">
        <Icon size={16} />
      </div>
      <div>
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-ink-muted">
          {label}
        </div>
        <div className="text-ink mt-0.5 text-sm md:text-base">{value}</div>
      </div>
    </div>
  );
  return (
    <li data-testid={testid}>
      {href ? (
        <a
          href={href}
          target={href.startsWith("http") ? "_blank" : undefined}
          rel="noreferrer"
        >
          {Content}
        </a>
      ) : (
        Content
      )}
    </li>
  );
}
